<template>
  <div class="capability-ticklist">
    <div class="ticklist-header">
      <h3 class="hub-title">{{ hubTitle }}</h3>
      <p class="hub-subtitle">{{ hubSubtitle }}</p>
    </div>

    <div class="capabilities-container">
      <div
        v-for="(capability, index) in capabilities"
        :key="index"
        class="capability-item"
        :class="{ 'selected': value.includes(index) }"
        @click="toggleCapability(index)"
      >
        <div
          class="tick-box"
          :style="value.includes(index) ? { backgroundColor: capability.color, borderColor: capability.color } : {}"
        >
          <v-icon v-if="value.includes(index)" color="white" size="18">mdi-check</v-icon>
        </div>

        <div class="capability-content">
          <div class="capability-header">
            <span class="capability-code" :style="{ backgroundColor: capability.color }">
              {{ capability.code }}
            </span>
            <span class="capability-short">{{ capability.shortStatement }}</span>
          </div>
          <p class="capability-full">{{ capability.fullStatement }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CapabilityTicklist',

  props: {
    hubTitle: {
      type: String,
      default: 'ELDAC Palliative Care Capabilities'
    },
    hubSubtitle: {
      type: String,
      default: 'Select the capabilities you want to develop'
    },
    value: {
      type: Array,
      default: () => []
    },
    items: {
      type: Array,
      default: () => []
    }
  },

  computed: {
    capabilities() {
      return this.items.map((item, index) => ({
        code: item.code || item.short || `C${index + 1}`,
        shortStatement: item.label || item.name || 'Unknown',
        fullStatement: item.fullStatement || item.subtext || item.description || item.label || item.name || 'No description available',
        color: item.group !== undefined
          ? ['#7A3C9A', '#3DB09E', '#3F95C8'][item.group] || '#3F95C8'
          : '#3F95C8'
      }));
    }
  },

  methods: {
    toggleCapability(index) {
      const newSelection = [...this.value];
      const currentIndex = newSelection.indexOf(index);
      if (currentIndex > -1) {
        newSelection.splice(currentIndex, 1);
      } else {
        newSelection.push(index);
      }
      newSelection.sort((a, b) => a - b);
      this.$emit('change', {
        selected: newSelection,
        capabilities: newSelection.map(i => this.capabilities[i])
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.capability-ticklist {
  max-width: 800px;
  margin: 0 auto;
}

.ticklist-header {
  text-align: center;
  margin-bottom: 2rem;

  .hub-title {
    color: #04748e;
    font-weight: 800;
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }

  .hub-subtitle {
    color: #0D7693;
    font-weight: 600;
    font-size: 1rem;
    margin: 0;
  }
}

.capabilities-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.capability-item {
  display: flex;
  align-items: flex-start;
  padding: 1.25rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;

  &:hover {
    background-color: #e4f0f3;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  &.selected {
    background-color: #e0f0ee;
    border-color: #04748e;

    .capability-content {
      color: #04748e;
    }

    .capability-short {
      font-weight: 700;
      color: #04748e;
    }
  }
}

.tick-box {
  width: 28px;
  height: 28px;
  border: 2px solid #ddd;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  flex-shrink: 0;
  margin-top: 2px;
  background-color: white;
  transition: all 0.3s ease;
}

.capability-content {
  flex: 1;
}

.capability-header {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.capability-code {
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-weight: 700;
  font-size: 0.875rem;
  min-width: 32px;
  text-align: center;
  margin-right: 0.75rem;
}

.capability-short {
  font-weight: 600;
  font-size: 1.1rem;
  color: #2c3e50;
}

.capability-full {
  margin: 0;
  color: #495057;
  font-size: 0.95rem;
  line-height: 1.4;
}

@media (max-width: 768px) {
  .capability-item {
    padding: 1rem;
  }

  .capability-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }

  .capability-short {
    font-size: 1rem;
  }

  .capability-full {
    font-size: 0.9rem;
  }
}
</style>
